import React, { useState, useMemo, useEffect } from 'react';
import api from '../services/api';
import { calculateContractMetrics, finite, tehranDateKey } from '../services/optionChainPricing';
import './OptionChain.css';
import { calculateOptionValueFromAPI } from '../services/optionPricing';

const displayNumber = value => finite(value) ? value.toLocaleString('fa-IR', {maximumFractionDigits: 2}) : 'ناموجود';

export default function OptionChain() {
  const [searchQuery, setSearchQuery] = useState('');
  const [optionSearchQuery, setOptionSearchQuery] = useState('');
  const [underlying, setUnderlying] = useState(null);
  const [options, setOptions] = useState([]);
  const [basePrices, setBasePrices] = useState({});
  const [loading, setLoading] = useState(false);

  // استیت‌های فیلتر و مرتب‌سازی
  const [filterType, setFilterType] = useState('all'); // options: all, call, put
  const [filterStatus, setFilterStatus] = useState('all'); // options: all, itm, otm
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // استیت‌های فیلترهای جدید
  const [filterDaysMin, setFilterDaysMin] = useState(0); // حداقل روز تا سررسید
  const [filterDaysMax, setFilterDaysMax] = useState(999); // حداکثر روز تا سررسید
  const [filterVolumeMin, setFilterVolumeMin] = useState(0); // حداقل حجم
  const [filterVolumeMax, setFilterVolumeMax] = useState(999999999); // حداکثر حجم
  const [filterDiffMin, setFilterDiffMin] = useState(null); // حداقل اختلاف با سهم (%)
  const [filterDiffMax, setFilterDiffMax] = useState(null); // حداکثر اختلاف با سهم (%)

  const [valuationDate, setValuationDate] = useState(() => tehranDateKey());

  // تابع پاک کردن همه فیلترها
  const clearAllFilters = () => {
    setFilterType('all');
    setFilterStatus('all');
    setFilterDaysMin(0);
    setFilterDaysMax(999);
    setFilterVolumeMin(0);
    setFilterVolumeMax(999999999);
    setFilterDiffMin(null);
    setFilterDiffMax(null);
    setOptionSearchQuery('');
  };

  const handleSearch = async (showLoading = true) => {
    if (!searchQuery) return;
    if (showLoading) setLoading(true);
    try {
      const data = await api.get(`/api/options-chain?base=${searchQuery}`);
      setUnderlying(data.underlying);
      setOptions(data.options);
      setBasePrices(data.base_prices || {});
    } catch (err) {
      console.error('Error fetching options:', err);
      alert('خطا در دریافت اطلاعات');
    }
    if (showLoading) setLoading(false);
  };

  // دریافت تمام آپشن‌های بازار در لود اولیه
  const fetchAllOptions = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const data = await api.get('/api/options-chain');
      setUnderlying(data.underlying);
      setOptions(data.options);
      setBasePrices(data.base_prices || {});
      setBasePrices(data.base_prices || {});
    } catch (err) {
      console.error('Error fetching all options:', err);
      alert('خطا در دریافت اطلاعات آپشن‌ها');
    }
    if (showLoading) setLoading(false);
  };

  useEffect(() => {
    fetchAllOptions();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setValuationDate(tehranDateKey());
      if (searchQuery) handleSearch(false);
      else fetchAllOptions(false);
    }, 30000);
    return () => clearInterval(interval);
  }, [searchQuery]);

  const enrichedOptions = useMemo(() => options.map(opt => {
    const base = underlying || basePrices[opt.base_symbol];
    const referenceBase = underlying?.symbol === opt.base_symbol ? underlying : basePrices[opt.base_symbol];
    const model = calculateOptionValueFromAPI(opt.symbol, referenceBase?.price, opt.price,
      opt.strike_price, opt.maturity_date, opt.base_symbol, opt.type,
      opt.price_yesterday, referenceBase?.price_yesterday, 0.40, 0.30, null, { asOf: valuationDate });
    const reference = model.isValid ? model.predictions.referencePrice : null;
    const difference = finite(reference) && reference > 0 && finite(opt.price) && opt.price >= 0 ? (opt.price - reference) / reference * 100 : null;
    return {...opt, ...calculateContractMetrics(opt, base?.price),
      modelDifferencePercent: finite(difference) ? difference : null,
      modelReferencePrice: model.isValid ? model.predictions.referencePrice : null,
      modelReferenceReason: model.isValid ? model.referenceReason : model.reason };
  }), [options, underlying, basePrices, valuationDate]);

  // ۲. اعمال فیلترها و مرتب‌سازی روی داده‌ها
  const processedOptions = useMemo(() => {
    let result = [...enrichedOptions];

    // اعمال فیلتر نوع آپشن
    if (filterType !== 'all') {
      result = result.filter(opt => opt.type === filterType);
    }

    // اعمال فیلتر وضعیت آپشن (در سود / در ضرر)
    if (filterStatus !== 'all') {
      result = result.filter(opt => opt.status.toLowerCase() === filterStatus);
    }

    // اعمال فیلتر جستجوی نماد آپشن
    if (optionSearchQuery.trim()) {
      const q = optionSearchQuery.trim().toLowerCase();
      result = result.filter(opt => opt.symbol && opt.symbol.toLowerCase().includes(q));
    }

    // اعمال فیلتر سررسید (تعداد روز باقیمانده)
    result = result.filter(opt => {
      const days = opt.daysLeft;
      if (!finite(days)) return filterDaysMin === 0 && filterDaysMax === 999;
      return days >= filterDaysMin && days <= filterDaysMax;
    });

    // اعمال فیلتر حجم معاملات
    result = result.filter(opt => {
      const vol = opt.volume;
      if (!finite(vol) || vol < 0) return filterVolumeMin === 0 && filterVolumeMax === 999999999;
      return vol >= filterVolumeMin && vol <= filterVolumeMax;
    });

    // اعمال فیلتر اختلاف با سهم
    if (filterDiffMin !== null) {
      result = result.filter(opt => finite(opt.diffWithStock) && opt.diffWithStock >= filterDiffMin);
    }
    if (filterDiffMax !== null) {
      result = result.filter(opt => finite(opt.diffWithStock) && opt.diffWithStock <= filterDiffMax);
    }

    // اعمال مرتب‌سازی (Sorting)
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // مدیریت مقادیر خالی (Null یا Undefined)
        if ((aValue == null || (typeof aValue === 'number' && !finite(aValue))) && (bValue == null || (typeof bValue === 'number' && !finite(bValue)))) return 0;
        if (aValue == null || (typeof aValue === 'number' && !finite(aValue))) return 1;
        if (bValue == null || (typeof bValue === 'number' && !finite(bValue))) return -1;

        if (typeof aValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue, 'fa')
            : bValue.localeCompare(aValue, 'fa');
        } else {
          return sortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        }
      });
    }

    return result;
  }, [enrichedOptions, filterType, filterStatus, sortConfig, optionSearchQuery, filterDaysMin, filterDaysMax, filterVolumeMin, filterVolumeMax, filterDiffMin, filterDiffMax]);

  // تابع مدیریت مرتب‌سازی با کلیک روی هدر ستون‌ها
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // گرفتن نشانه گرافیکی وضعیت سورت برای هدر جدول
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  return (
    <div className="option-chain-container">
      <div className="header-section">
        <h1>زنجیره آپشن و تحلیل بلک‌شولز</h1>
        
        <div className="search-box">
          <input 
            className="search-input"
            type="text" 
            placeholder="نماد پایه (مثل: شستا)" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button className="search-btn" onClick={handleSearch} disabled={loading}>
            {loading ? 'در حال جستجو...' : 'جستجو و دریافت آپشن‌ها'}
          </button>
        </div>
      </div>

      <div className="option-chain-safety-warning" role="status">
        <strong>سیگنال‌های معاملاتی موقتاً غیرفعال هستند؛ صحت محاسبات و تازگی داده‌ها هنوز تأیید نشده است.</strong>
        <p>تا زمان تأیید صحت محاسبات و به‌روزبودن داده‌ها، سیگنال معاملاتی صادر نمی‌شود.</p>
        <p className="option-chain-model-note">قیمت نظری بلک‌شولز و برآورد تیلور صرفاً برآوردهای تحلیلی تأییدنشده‌اند؛ قیمت عادلانه قابل اتکا یا توصیه خرید و فروش نیستند.</p>
        <p className="option-chain-model-note">مدل اروپایی بدون سود نقدی؛ نرخ سالانه فرضی ۳۰٪ و نوسان مرجع ۴۰٪. روزهای تقویمی تهران / ۳۶۵؛ هزینه و ضریب قرارداد لحاظ نشده است. تازگی و همگامی قیمت‌ها تأیید نشده؛ برآورد تیلور بدون زمان مرجع معتبر ناموجود است.</p>
      </div>

      {underlying && (
        <div className="underlying-info">
          <h2>نماد پایه: {underlying.symbol}</h2>
          <p>آخرین قیمت ذخیره‌شده: <strong>{displayNumber(underlying.price)}</strong> ریال</p>
          <p style={{ fontSize: '12px', opacity: 0.7 }}>زمان ثبت داده (بدون تأیید تازگی): {underlying.last_update}</p>
        </div>
      )}

      {/* بخش کنترل فیلترها */}
      {options.length > 0 && (
        <div className="filter-bar">
          <div className="filter-group">
            <label>نوع اختیار:</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="all">همه</option>
              <option value="call">اختیار خرید</option>
              <option value="put">اختیار فروش</option>
            </select>
          </div>

          <div className="filter-group">
            <label>موقعیت نسبت به اعمال:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">همه</option>
              <option value="itm">داخل پول (ITM)</option>
              <option value="otm">خارج پول (OTM)</option>
              <option value="atm">روی قیمت اعمال (ATM)</option>
            </select>
          </div>

          <div className="filter-group">
            <label>سررسید (روز):</label>
            <input
              type="number"
              className="filter-input-small"
              placeholder="از"
              value={filterDaysMin === 0 ? '' : filterDaysMin}
              onChange={(e) => setFilterDaysMin(e.target.value === '' ? 0 : Number(e.target.value))}
              min="0"
            />
            <span style={{ margin: '0 5px', color: '#888' }}>تا</span>
            <input
              type="number"
              className="filter-input-small"
              placeholder="تا"
              value={filterDaysMax === 999 ? '' : filterDaysMax}
              onChange={(e) => setFilterDaysMax(e.target.value === '' ? 999 : Number(e.target.value))}
              min="0"
            />
          </div>

          <div className="filter-group">
            <label>حجم معاملات:</label>
            <input
              type="number"
              className="filter-input-small"
              placeholder="از"
              value={filterVolumeMin === 0 ? '' : filterVolumeMin}
              onChange={(e) => setFilterVolumeMin(e.target.value === '' ? 0 : Number(e.target.value))}
              min="0"
            />
            <span style={{ margin: '0 5px', color: '#888' }}>تا</span>
            <input
              type="number"
              className="filter-input-small"
              placeholder="تا"
              value={filterVolumeMax === 999999999 ? '' : filterVolumeMax}
              onChange={(e) => setFilterVolumeMax(e.target.value === '' ? 999999999 : Number(e.target.value))}
              min="0"
            />
          </div>

          <div className="filter-group">
            <label>اختلاف با سهم (%):</label>
            <input
              type="number"
              className="filter-input-small"
              placeholder="از"
              value={filterDiffMin === null ? '' : filterDiffMin}
              onChange={(e) => setFilterDiffMin(e.target.value === '' ? null : parseFloat(e.target.value))}
            />
            <span style={{ margin: '0 5px', color: '#888' }}>تا</span>
            <input
              type="number"
              className="filter-input-small"
              placeholder="تا"
              value={filterDiffMax === null ? '' : filterDiffMax}
              onChange={(e) => setFilterDiffMax(e.target.value === '' ? null : parseFloat(e.target.value))}
            />
          </div>

          <div className="filter-group option-search-group">
            <label>جستجوی نماد آپشن:</label>
            <div className="option-search-input-wrapper">
              <span className="option-search-icon">🔎</span>
              <input
                className="option-search-input"
                type="text"
                placeholder="مثلاً ضخود5053"
                value={optionSearchQuery}
                onChange={(e) => setOptionSearchQuery(e.target.value)}
              />
              {optionSearchQuery && (
                <button
                  className="option-search-clear"
                  onClick={() => setOptionSearchQuery('')}
                  title="پاک کردن"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="filter-group">
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              🔄 پاک کردن فیلترها
            </button>
          </div>
        </div>
      )}

      <p className="option-chain-model-note">قیمت مبنای مدل از همان محاسبهٔ پورتفوی، با IV ثابت مشاهده قبلی و نرخ سالانهٔ پیوستهٔ ۳۰٪ به دست می‌آید. فاصلهٔ مشاهده قبلی یک روز تقویمی فرض شده؛ تاریخ واقعی آن در منبع موجود نیست.</p>

      {processedOptions.length > 0 ? (
        <div className="table-container">
          <table className="options-table">
            <thead>
              <tr>
                <th className="sortable-header" onClick={() => requestSort('symbol')}>
                  نماد آپشن <span className="sort-icon">{getSortIcon('symbol')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('type')}>
                  نوع <span className="sort-icon">{getSortIcon('type')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('price')}>
                  آخرین قیمت ثبت‌شده آپشن <span className="sort-icon">{getSortIcon('price')}</span>
                </th>
                <th className="sortable-header volume-header" onClick={() => requestSort('volume')}>
                  حجم معاملات <span className="sort-icon">{getSortIcon('volume')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('strike_price')}>
                  قیمت اعمال <span className="sort-icon">{getSortIcon('strike_price')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('daysLeft')}>
                  سررسید (روز باقیمانده) <span className="sort-icon">{getSortIcon('daysLeft')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('status')}>
                  موقعیت (ITM/ATM/OTM) <span className="sort-icon">{getSortIcon('status')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('bsValue')}>
                  قیمت نظری بلک‌شولز (تأییدنشده) <span className="sort-icon">{getSortIcon('bsValue')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('bubble')}>
                  اختلاف با مدل نظری (%) <span className="sort-icon">{getSortIcon('bubble')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('diffWithStock')}>
                  اختلاف با سهم <span className="sort-icon">{getSortIcon('diffWithStock')}</span>
                </th>
                <th className="sortable-header live-fair-value-header" onClick={() => requestSort('modelReferencePrice')}>
                  قیمت مبنای مدل (ریال) <span className="sort-icon">{getSortIcon('modelReferencePrice')}</span>
                </th>
                <th className="sortable-header" onClick={() => requestSort('modelDifferencePercent')}>
                  اختلاف قیمت آپشن با مبنای مدل (%) <span className="sort-icon">{getSortIcon('modelDifferencePercent')}</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {processedOptions.map((opt) => (
                <tr key={opt.symbol}>
                  <td className="symbol-cell">{opt.symbol}{opt.qualityFlags.includes('NEGATIVE_TIME_VALUE') && <small style={{display: 'block', color: '#fbbf24'}} title={`ارزش زمانی منفی حفظ شده است: ${opt.timeValue}؛ اختلاف داده/مدل، نه سیگنال معامله`}>ارزش زمانی منفی</small>}</td>
                  <td className={opt.type === 'call' ? 'call-text' : 'put-text'}>
                    {opt.type === 'call' ? 'اختیار خرید' : opt.type === 'put' ? 'اختیار فروش' : 'نوع نامعتبر'}
                  </td>
                  <td>{displayNumber(opt.price)}</td>
                  <td className="volume-cell">
                    {finite(opt.volume) && opt.volume >= 0 ? displayNumber(opt.volume) : 'ناموجود'}
                  </td>
                  <td style={{ fontWeight: 'bold' }}>
                    {finite(opt.strike_price) && opt.strike_price > 0 ? displayNumber(opt.strike_price) : 'ناموجود'}
                  </td>
                  <td>
                    {opt.maturity_date ? (
                      <span style={{ fontSize: '14px' }}>
                        <span style={{ display: 'block', color: '#888', fontSize: '11px' }}>{opt.maturity_date}</span>
                        {opt.expiry.status === 'INVALID' ? 'تاریخ نامعتبر' : opt.expiry.status === 'EXPIRED' ? 'منقضی' : opt.expiry.status === 'SAME_DAY' ? 'سررسید امروز؛ ساعت نامشخص' : `${opt.daysLeft} روز`}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ fontWeight: 'bold', color: '#cbd5e1' }}>
                    {opt.status}
                  </td>
                  <td title={opt.bs.reason || 'اروپایی، بدون سود نقدی؛ نرخ ۳۰٪ و نوسان فرضی ۴۰٪'}>{displayNumber(opt.bsValue)}</td>
                  <td dir="ltr" style={{ color: opt.bubble > 0 ? '#f44336' : (opt.bubble < 0 ? '#4caf50' : '#ccc') }}>
                    {opt.bubble !== null ? `% ${displayNumber(opt.bubble)}` : '-'}
                  </td>
                  <td dir="ltr" style={{ color: opt.diffWithStock > 0 ? '#4caf50' : (opt.diffWithStock < 0 ? '#f44336' : '#ccc') }}>
                    {opt.diffWithStock !== null ? `% ${displayNumber(opt.diffWithStock)}` : '-'}
                  </td>
                  <td className="live-fair-value-cell" title={opt.modelReferenceReason || 'بلک‌شولز با IV ثابت مشاهده قبلی؛ نرخ سالانه پیوسته ۳۰٪؛ فرض فاصله یک روز تقویمی؛ تاریخ واقعی مشاهده قبلی در منبع موجود نیست'}>
                    <span className={finite(opt.modelReferencePrice) ? 'live-fair-value-amount' : 'live-fair-value-unavailable'}>{displayNumber(opt.modelReferencePrice)}</span>
                  </td>
                  <td className="model-price-difference-cell" dir="ltr" title="(آخرین قیمت آپشن − قیمت مبنای مدل) ÷ قیمت مبنای مدل × ۱۰۰" style={{ color: opt.modelDifferencePercent > 0 ? '#4caf50' : opt.modelDifferencePercent < 0 ? '#f44336' : '#cbd5e1', fontWeight: 'bold' }}>
                    {finite(opt.modelDifferencePercent) ? `${opt.modelDifferencePercent > 0 ? '+' : opt.modelDifferencePercent < 0 ? '−' : ''}${displayNumber(Math.abs(opt.modelDifferencePercent))}٪` : 'ناموجود'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        options.length > 0 && <p style={{ textAlign: 'center', marginTop: '20px', color: '#aaa' }}>هیچ آپشنی با این فیلترها یافت نشد.</p>
      )}
    </div>
  );
}
